import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Image as ImageIcon } from 'lucide-react';

import { api } from '../lib/api.js';
import { Button, Card, Field, SectionHeading, Select, Skeleton, Textarea, useToast } from '../components/ui.js';

/**
 * What goes on a printed receipt.
 *
 * The shop had all of this in Loyverse and none of it here, so the slip this
 * app printed was whatever the code decided it should say. These are the parts
 * a customer actually reads — the mark at the top, the couple of lines saying
 * who the shop is, the handles at the bottom — and they belong to the shop,
 * not to whoever wrote the renderer.
 *
 * Header and footer are free text rather than fields, for the same reason
 * Loyverse does it that way: "Artisan Breads • Pastries • Coffee" and the
 * opening hours are not things an address book can produce.
 */
export function ReceiptTab() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const settings = useQuery({ queryKey: ['settings'], queryFn: api.settings.get });

  const receipt = settings.data?.receipt;
  const business = settings.data?.business;

  const [draft, setDraft] = useState<
    Partial<{
      headerText: string;
      footerText: string;
      showLogo: boolean;
      showCustomer: boolean;
      showCashier: boolean;
      showItemNotes: boolean;
      copies: number;
      logoUrl: string | null;
    }>
  | null>(null);

  /** Blank lines out: an empty line is wasted roll on every single sale. */
  const toLines = (text: string) =>
    text
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

  const save = useMutation({
    mutationFn: () =>
      api.settings.update({
        receipt: {
          ...receipt,
          headerLines: toLines(draft?.headerText ?? (receipt?.headerLines ?? []).join('\n')),
          footerLines: toLines(draft?.footerText ?? (receipt?.footerLines ?? []).join('\n')),
          showLogo: draft?.showLogo ?? receipt?.showLogo ?? false,
          showCustomer: draft?.showCustomer ?? receipt?.showCustomer ?? true,
          showCashier: draft?.showCashier ?? receipt?.showCashier ?? true,
          showItemNotes: draft?.showItemNotes ?? receipt?.showItemNotes ?? true,
          copies: draft?.copies ?? receipt?.copies ?? 1,
        } as never,
        // The logo lives on the business record, so it only travels when it changed.
        ...(draft?.logoUrl !== undefined ? { business: { ...business, logoUrl: draft.logoUrl } as never } : {}),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      setDraft(null);
      toast.push({ tone: 'ok', title: 'Receipt settings saved' });
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not save', body: error.message }),
  });

  if (!receipt || !business) return <Skeleton className="h-64" />;

  const headerText = draft?.headerText ?? receipt.headerLines.join('\n');
  const footerText = draft?.footerText ?? receipt.footerLines.join('\n');
  const logoUrl = draft?.logoUrl !== undefined ? draft.logoUrl : business.logoUrl;
  const flag = (key: 'showLogo' | 'showCustomer' | 'showCashier' | 'showItemNotes') =>
    (draft?.[key] as boolean | undefined) ?? receipt[key];

  /**
   * Prepare a logo for a thermal head, here, rather than asking the shop to.
   *
   * A brand logo is a quarter-megabyte colour PNG at a few thousand pixels
   * across. A receipt needs about 270 dots of pure black and white. Refusing
   * the file and telling somebody to go and resize it is the kind of answer
   * that means the logo never gets added — so the picture is shrunk and
   * thresholded on the way in.
   *
   * Thresholded rather than merely greyscaled: the printer has one ink and no
   * half-tones, so every pixel has to be decided here. Left to the driver it
   * dithers, and a fine line drawing turns into a grey mist.
   */
  const readLogo = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const image = new Image();
      image.onload = () => {
        // 34mm at 203dpi is about 270 dots — the width the receipt gives it.
        const maxWidth = 270;
        const scale = Math.min(1, maxWidth / image.width);
        const width = Math.max(1, Math.round(image.width * scale));
        const height = Math.max(1, Math.round(image.height * scale));

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext('2d');
        if (!context) {
          toast.push({ tone: 'warn', title: 'Could not read that image' });
          return;
        }

        // White behind, because a transparent PNG would otherwise threshold
        // its own empty background to solid black.
        context.fillStyle = '#fff';
        context.fillRect(0, 0, width, height);
        context.drawImage(image, 0, 0, width, height);

        const pixels = context.getImageData(0, 0, width, height);
        const data = pixels.data;
        for (let i = 0; i < data.length; i += 4) {
          const luma = 0.299 * data[i]! + 0.587 * data[i + 1]! + 0.114 * data[i + 2]!;
          const ink = luma < 170 ? 0 : 255;
          data[i] = ink;
          data[i + 1] = ink;
          data[i + 2] = ink;
          data[i + 3] = 255;
        }
        context.putImageData(pixels, 0, 0);

        setDraft((current) => ({ ...current, logoUrl: canvas.toDataURL('image/png') }));
        toast.push({
          tone: 'ok',
          title: 'Logo ready',
          body: `Resized to ${width}×${height} and set to pure black and white for the printer.`,
        });
      };
      image.onerror = () => toast.push({ tone: 'warn', title: 'That file is not an image this browser can read' });
      image.src = String(reader.result);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-3">
      <Card className="space-y-4 p-4">
        <SectionHeading
          title="Logo"
          hint="Printed at the top of the receipt. Black and white only — thermal paper has no greys."
        />

        <div className="flex items-start gap-4">
          <div className="grid size-28 shrink-0 place-items-center overflow-hidden rounded-[var(--radius-md)] border border-[var(--color-line)] bg-white">
            {logoUrl ? (
              <img src={logoUrl} alt="Receipt logo" className="max-h-full max-w-full object-contain" />
            ) : (
              <ImageIcon className="size-8 text-[var(--color-ink-faint)]" />
            )}
          </div>

          <div className="min-w-0 flex-1 space-y-2">
            <input
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) readLogo(file);
                event.target.value = '';
              }}
              className="block w-full text-sm file:mr-3 file:rounded-[var(--radius-sm)] file:border file:border-[var(--color-line-strong)] file:bg-transparent file:px-3 file:py-1.5 file:text-sm"
            />
            <p className="text-xs text-[var(--color-ink-faint)]">
              Any size — it is resized and set to pure black and white for the printer when you pick it. A plain,
              high-contrast mark prints best; a photograph will come out as a silhouette.
            </p>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={flag('showLogo')}
                onChange={(event) => setDraft((current) => ({ ...current, showLogo: event.target.checked }))}
              />
              Print the logo on receipts
            </label>
            {logoUrl && (
              <Button size="sm" variant="ghost" onClick={() => setDraft((current) => ({ ...current, logoUrl: null }))}>
                Remove logo
              </Button>
            )}
          </div>
        </div>
      </Card>

      <Card className="space-y-4 p-4">
        <SectionHeading title="Header and footer" hint="One line each. These print under the shop name and at the very bottom." />

        <Field label="Header" hint="What you do, where you are, when you are open.">
          <Textarea
            rows={5}
            value={headerText}
            onChange={(event) => setDraft((current) => ({ ...current, headerText: event.target.value }))}
            placeholder={'Artisan Breads • Pastries • Coffee\n126 MMV Building\nM. L. Quezon Ave., Hagonoy, Taguig\nMon–Fri • 9:00 AM – 6:00 PM'}
            className="resize-y"
          />
        </Field>

        <Field label="Footer" hint="The last thing they read. Social handles and a phone number earn their place here.">
          <Textarea
            rows={4}
            value={footerText}
            onChange={(event) => setDraft((current) => ({ ...current, footerText: event.target.value }))}
            placeholder={'Facebook & Instagram:\n@ANASBAKERYPH\n0960 857 4458'}
            className="resize-y"
          />
        </Field>
      </Card>

      <Card className="space-y-3 p-4">
        <SectionHeading title="What to show" />

        {(
          [
            ['showCustomer', 'Customer name', 'Their name, when the sale is attached to a client.'],
            ['showCashier', 'Who served them', 'The staff member who rang it up.'],
            ['showItemNotes', 'Item notes', 'Requests written against a line — "no walnuts", "cut in half".'],
          ] as const
        ).map(([key, label, hint]) => (
          <label key={key} className="flex items-start gap-2.5">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={flag(key)}
              onChange={(event) => setDraft((current) => ({ ...current, [key]: event.target.checked }))}
            />
            <span className="min-w-0">
              <span className="block text-sm">{label}</span>
              <span className="block text-xs text-[var(--color-ink-faint)]">{hint}</span>
            </span>
          </label>
        ))}

        <Field label="Copies" hint="Two prints one for the customer and one for the kitchen.">
          <Select
            value={String(draft?.copies ?? receipt.copies)}
            onChange={(event) => setDraft((current) => ({ ...current, copies: Number(event.target.value) }))}
            className="w-32"
          >
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3">3</option>
          </Select>
        </Field>
      </Card>

      <div className="flex justify-end">
        <Button variant="primary" loading={save.isPending} disabled={!draft} onClick={() => save.mutate()}>
          Save receipt settings
        </Button>
      </div>
    </div>
  );
}
